import { useLayoutEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { getSubsetAndItemParams } from '../../../../utils/getSubsetAndItemParams';
import { GlobalLocationStateType } from '../../../../types/types';
import useRoutes from '../../../../hooks/useRoutes';

type UseDetailDisabledTabFallbackProps = {
  enabled: boolean;
  datasetId: string;
  subsets: { datasetId: string }[] | undefined;
  slaveTabIndex: number;
  setSlaveTabIndex: (value: number) => void;
};

// Detail tab hidden (dataset.useDetail === false): slot 0 is an invisible
// placeholder, so an expanded row must never leave it active — TabView would
// render an empty panel and slaveTabIndex 0 breaks the subsets[index - 1] math.
// Layout effect so the correction lands pre-paint, with no empty frame.
export const useDetailDisabledTabFallback = ({
  enabled,
  datasetId,
  subsets,
  slaveTabIndex,
  setSlaveTabIndex,
}: UseDetailDisabledTabFallbackProps): void => {
  const { pathname } = useLocation<GlobalLocationStateType>();
  const history = useHistory();
  const routes = useRoutes();

  useLayoutEffect(() => {
    if (!enabled || !subsets?.length) return;

    const { itemId, subsetId } = getSubsetAndItemParams(pathname);
    if (itemId === undefined) return;

    // URL carries a subset: only adopt its index while slot 0 is still active,
    // otherwise the existing pathname sync owns the switch.
    if (subsetId) {
      if (slaveTabIndex > 0) return;
      const urlIndex =
        subsets.findIndex((subset) => subset.datasetId === subsetId) + 1;
      if (urlIndex > 0) setSlaveTabIndex(urlIndex);
      return;
    }

    // No /subset/ segment: useSyncSubsetIdToTab would null the active subsetId,
    // so put the remembered tab (or the first) back into the URL.
    const targetIndex =
      slaveTabIndex > 0 && slaveTabIndex <= subsets.length ? slaveTabIndex : 1;
    const targetSubsetId = subsets[targetIndex - 1]?.datasetId;
    if (!targetSubsetId) return;

    if (slaveTabIndex !== targetIndex) setSlaveTabIndex(targetIndex);

    // Carry router state — the form-return flow still reads it after findRowCleanup.
    history.replace(
      routes.subset(datasetId, itemId, targetSubsetId),
      history.location.state
    );
  }, [pathname, enabled, slaveTabIndex]);
};
